import React, { FC } from 'react';
import { ifProp } from 'styled-tools';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useRouter } from 'next/router';
import { isSpecialPrice, mq } from '$lib/helpers';
import { ISimpleProduct } from '$models';
import { Flex } from '$components/layouts';
import { useGtm } from '$hooks';
import { RebatePercent } from './rebate-percent';
import { SvgIcon } from '$components/elements/svg-icon';
import { Link } from '$components/elements/link';

type VariantSelectorProps = {
    variant?: ISimpleProduct;
    selectedVariantNumber?: string;
};

export const VariantSelector: FC<VariantSelectorProps> = ({
    variant,
    selectedVariantNumber,
}: VariantSelectorProps): JSX.Element => {
    const { replace } = useRouter();
    const { trackVirtualPageview } = useGtm();
    const theme = useTheme();

    const getVariantIcon = (itemBinding?: string) => {
        if (!itemBinding) {
            return 'book';
        }
        switch (itemBinding.toLocaleLowerCase()) {
            case 'hardback':
            case 'indbundet':
            case 'hæftet':
            case 'paperback':
                return 'book';
            case 'e-bog':
            case 'ebog':
                return 'smartPhone';
            case 'lydbog':
                return 'headphones';
            case 'cd':
            case 'mp3-cd':
                return 'disc';
            default:
                return 'book';
        }
    };

    const onVariantSelect = (e: React.MouseEvent, variant: ISimpleProduct) => {
        e.preventDefault();
        trackVirtualPageview(variant?.productUrl);
        replace(variant?.productUrl || '');
    };

    return (
        <>
            {!!variant?.productUrl && (
                <VariantSelectorContainer>
                    <VariantSelectorLink
                        href={variant.productUrl}
                        scroll={false}
                        replace
                        expansive
                        onClick={(e) => onVariantSelect(e, variant)}
                    >
                        <StyledVariantSelector isSelected={selectedVariantNumber === variant?.itemNumber}>
                            {!!getVariantIcon(variant?.itemBinding) && (
                                <SvgIcon size={24} svg={getVariantIcon(variant?.itemBinding)} />
                            )}
                            <ItemBinding>{variant?.itemGlobalBinding || variant?.itemBinding}</ItemBinding>

                            {isSpecialPrice(variant?.activePrice?.accountRelation) ? (
                                <Price isSelected={selectedVariantNumber === variant?.itemNumber} hasRebate>
                                    {variant.activeAmountAsText}
                                </Price>
                            ) : (
                                <>
                                    <Price
                                        isSelected={selectedVariantNumber === variant?.itemNumber}
                                        hasRebate={!!variant.inActivePrice}
                                    >
                                        {variant.activeAmountAsText}
                                    </Price>
                                    {variant.inActivePrice && (
                                        <Flex column alignItems="center">
                                            <InActivePrice>{variant.inActiveAmountAsText}</InActivePrice>
                                            <RebatePercent
                                                isSelected={selectedVariantNumber === variant?.itemNumber}
                                            >{`-${Math.round(Number(variant.rebatePercent))}%`}</RebatePercent>
                                        </Flex>
                                    )}
                                </>
                            )}
                            {selectedVariantNumber === variant?.itemNumber && (
                                <SvgContainer>
                                    <VariantCheckmark svg="checkmark" size={20} color={theme.colors.white} />
                                </SvgContainer>
                            )}
                        </StyledVariantSelector>
                    </VariantSelectorLink>
                </VariantSelectorContainer>
            )}
        </>
    );
};

const VariantSelectorLink = styled(Link)({
    display: 'grid',
});

const VariantSelectorContainer = styled.div(() => ({
    display: 'grid',
    flex: '0 1 50%',
    [mq('md')]: {
        flex: '1 1 25%',
    },
}));

const StyledVariantSelector = styled.div<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
    position: 'relative',
    border: isSelected ? `2px solid ${theme.colors.primaryTheme}` : `1px solid ${theme.colors.grey5}`,
    // backgroundColor: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.space[1],
    color: theme.colors.black,
    height: '100%',
    cursor: isSelected ? 'default' : 'pointer',
    borderRadius: theme.general.borderRadius,
    padding: theme.space[5],
    opacity: 1,
    transition: 'height 200ms ease-in-out, opacity 150ms ease-in-out 50ms',

    '&:hover': {
        // backgroundColor: isSelected ? theme.colors.white : theme.colors.grey5,
        backgroundColor: isSelected ? 'transparent' : theme.colors.grey5,
    },
}));

const Price = styled.div<{ hasRebate: boolean; isSelected: boolean }>(
    ({ theme }) => ({
        fontWeight: theme.fontWeights.bold,
        fontSize: theme.fontSizes.md,
        textAlign: 'center',
    }),
    ifProp({ hasRebate: true }, ({ theme, isSelected }) => ({
        color: isSelected === false ? theme.colors.black : theme.colors.primaryTheme,
    }))
);

const InActivePrice = styled.div(({ theme }) => ({
    position: 'absolute',
    fontSize: theme.fontSizes.xxxs,
    color: theme.colors.primaryGrey,
    textDecoration: 'line-through',
    textAlign: 'center',
}));

const SvgContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: '10px',
    right: '10px',
});

const ItemBinding = styled.h2(({ theme }) => ({
    ...theme.mixins.useTextStyle('bodyLarge'),
    marginBottom: 0,
    letterSpacing: '0',
    paddingTop: theme.space[2],
    paddingBottom: theme.space[1],
}));

const VariantCheckmark = styled(SvgIcon)(({ theme }) => ({
    backgroundColor: theme.colors.primaryTheme,
    padding: '4px',
    borderRadius: '50%',
    marginBottom: '-8px',
}));
